/**
 * Reusable combobox-style picker for Managers / Development Leaders.
 * Uses the Command + Popover shadcn primitives.
 * Pass `value` (manager_id), `onChange(manager_id, manager_name)`, and optional `disabled`.
 * Empty string is treated as "no manager assigned" and triggers a clear.
 */
import { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { managersAPI } from '@/lib/api';

export function ManagerSelector({
  value,
  valueName,
  onChange,
  disabled = false,
  placeholder = 'Select a manager…',
  className = '',
  testId = 'manager-selector',
}) {
  const [open, setOpen] = useState(false);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    managersAPI.list({ active_only: true })
      .then((r) => { if (!cancelled) setManagers(r.data.managers || []); })
      .catch(() => { if (!cancelled) setManagers([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const selected = useMemo(
    () => managers.find((m) => m.manager_id === value) || (value ? { manager_id: value, full_name: valueName || value } : null),
    [managers, value, valueName],
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return managers;
    const q = search.toLowerCase();
    return managers.filter(
      (m) =>
        (m.full_name || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q),
    );
  }, [managers, search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={cn('w-full justify-between font-normal', className)}
        >
          <span className={cn('truncate', !selected && 'text-slate-400')}>
            {selected ? selected.full_name : placeholder}
          </span>
          <div className="flex items-center gap-1 shrink-0">
            {selected && !disabled && (
              <X
                className="h-4 w-4 opacity-60 hover:opacity-100"
                onClick={(e) => { e.stopPropagation(); onChange('', null); }}
                data-testid={`${testId}-clear`}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by name or email…"
            value={search}
            onValueChange={setSearch}
            data-testid={`${testId}-search`}
          />
          <CommandList>
            <CommandEmpty>{loading ? 'Loading…' : 'No managers found.'}</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__none__"
                onSelect={() => { onChange('', null); setOpen(false); }}
                data-testid={`${testId}-none`}
              >
                <Check className={cn('mr-2 h-4 w-4', !value ? 'opacity-100' : 'opacity-0')} />
                <span className="italic text-slate-500">No manager assigned</span>
              </CommandItem>
              {filtered.map((m) => (
                <CommandItem
                  key={m.manager_id}
                  value={m.manager_id}
                  onSelect={() => { onChange(m.manager_id, m.full_name); setOpen(false); }}
                  data-testid={`${testId}-option-${m.manager_id}`}
                >
                  <Check className={cn('mr-2 h-4 w-4', value === m.manager_id ? 'opacity-100' : 'opacity-0')} />
                  <div className="flex-1">
                    <div className="text-sm">{m.full_name}</div>
                    {m.email && <div className="text-xs text-slate-500">{m.email}</div>}
                  </div>
                  {m.npn && <span className="text-xs text-slate-400 ml-2">NPN {m.npn}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
